import { useMemo, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { toggleDsPick, setDsViewPreference } from 'redux/mode/actions';
import useScrollMemory from 'hooks/useScrollMemory';
import { IconType } from 'components/Icon';
import ViewToggle from 'components/Widget/ViewToggle';
import { DesignSystemToken } from 'designSystem/types';
import { getColorString } from 'designSystem/content/util';
import { groupColorsForGrid, ColorFamily } from './util';
import ColorTable from './ColorTable';
import * as Base from '../../styles';
import * as S from './styles';

const ColorsView = () => {
	const dispatch = useDispatch();
	const { designSystemContent, activeTab, selectedPicks, colorsViewMode, gridVisible } =
		useSelector(
			(r: RootReducer) => ({
				designSystemContent: r.mode.designSystemContent,
				activeTab: selectActiveDsTab(r.mode),
				selectedPicks: r.mode.selectedDsPicks,
				colorsViewMode: r.mode.dsViewPreferences.colorsViewMode ?? 'grid',
				gridVisible: r.mode.dsViewPreferences.colorsGridVisible ?? true,
			}),
			shallowEqual
		);

	const scrollRef = useScrollMemory(activeTab?.id ?? null);

	const colorTokens = useMemo(() => {
		if (!designSystemContent?.theme) return [];
		const byCategory = designSystemContent.theme.byCategory;
		const colorTokens = byCategory.color || [];
		// Also include any tokens from other categories that have color values
		const others = designSystemContent.theme.all.filter(
			(t) => t.category !== 'color' && t.value.kind === 'color'
		);
		return [...colorTokens, ...others];
	}, [designSystemContent?.theme]);

	const gridData = useMemo(() => groupColorsForGrid(colorTokens), [colorTokens]);
	const hasGrid = gridData.families.length > 0;

	const isTokenSelected = (token: DesignSystemToken) =>
		selectedPicks.some((p) => p.kind === 'token' && p.cssVariable === token.cssVariable);

	const onSwatchClick = (token: DesignSystemToken) => {
		dispatch(toggleDsPick({ kind: 'token', cssVariable: token.cssVariable }));
	};

	const onToggleGrid = () => {
		dispatch(setDsViewPreference({ colorsGridVisible: !gridVisible }));
	};

	// Tokens shown in the tiles/list area depend on whether the grid is visible
	const tileTokens = gridVisible ? gridData.ungrouped : colorTokens;

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderViewToggle = () => (
		<ViewToggle
			options={[
				{ value: 'grid', icon: IconType.GRID, title: 'Tile view' },
				{ value: 'list', icon: IconType.STACK, title: 'List view' },
			]}
			value={colorsViewMode}
			onChange={(v) => dispatch(setDsViewPreference({ colorsViewMode: v as 'grid' | 'list' }))}
		/>
	);

	const renderFamilyGrid = (families: ColorFamily[], allShades: string[]) => {
		if (families.length === 0) return null;
		return (
			<S.GridWrapper>
				<S.ColorGrid
					style={{
						gridTemplateColumns: `auto repeat(${allShades.length}, minmax(48px, 80px))`,
					}}
				>
					<div />
					{allShades.map((shade) => (
						<S.HeaderCell key={shade}>{shade}</S.HeaderCell>
					))}

					{families.map((family) => {
						const shadeMap = new Map(family.shades.map((s) => [s.shade, s.token]));
						return (
							<Fragment key={family.family}>
								<S.FamilyLabel>
									{family.family}
								</S.FamilyLabel>
								{allShades.map((shade) => {
									const token = shadeMap.get(shade);
									if (!token) {
										return <S.EmptyCell key={`${family.family}-${shade}`} />;
									}
									const color = getColorString(token);
									const selected = isTokenSelected(token);
									return (
									<S.SwatchCell
										key={token.id}
										type="button"
										$color={color}
										$selected={selected}
										aria-pressed={selected}
										onClick={() => onSwatchClick(token)}
									>
											<S.SwatchTooltip>
												{token.cssVariable}
												<br />
												{color}
											</S.SwatchTooltip>
										</S.SwatchCell>
									);
								})}
							</Fragment>
						);
					})}
				</S.ColorGrid>
			</S.GridWrapper>
		);
	};

	const renderTiles = (tokens: DesignSystemToken[]) => (
		<S.UngroupedGrid>
			{tokens.map((token) => {
				const color = getColorString(token);
				const selected = isTokenSelected(token);
				return (
				<S.UngroupedSwatch
					key={token.id}
					type="button"
					$selected={selected}
					aria-pressed={selected}
					onClick={() => onSwatchClick(token)}
				>
						<S.UngroupedSwatchTooltip>{color}</S.UngroupedSwatchTooltip>
						<S.UngroupedSwatchColor $color={color} />
						<S.UngroupedSwatchLabel>
							<S.UngroupedSwatchName>{token.name}</S.UngroupedSwatchName>
							<S.UngroupedSwatchVariable>
								{token.cssVariable}
							</S.UngroupedSwatchVariable>
						</S.UngroupedSwatchLabel>
					</S.UngroupedSwatch>
				);
			})}
		</S.UngroupedGrid>
	);

	const renderTileSection = (tokens: DesignSystemToken[], showHeader: boolean) => {
		if (tokens.length === 0) return null;
		return (
			<S.UngroupedSection>
				{showHeader && (
					<S.UngroupedHeader>
						<S.UngroupedTitle>Other Colors</S.UngroupedTitle>
						{renderViewToggle()}
					</S.UngroupedHeader>
				)}
				{colorsViewMode === 'list' ? <ColorTable tokens={tokens} /> : renderTiles(tokens)}
			</S.UngroupedSection>
		);
	};

	return (
		<Base.SectionBase ref={scrollRef}>
			<Base.SectionHeader>
				<Base.SectionTitle>Colors</Base.SectionTitle>
				{colorTokens.length > 0 && (
					<S.HeaderControls>
						{(!gridVisible || !hasGrid) && renderViewToggle()}
					{hasGrid && (
						<ViewToggle
							options={[
								{
									value: 'toggle',
									icon: IconType.LAYOUT_MASONRY,
									title: gridVisible ? 'Hide color grid' : 'Show color grid',
								},
							]}
							value={gridVisible ? 'toggle' : ''}
							onChange={onToggleGrid}
						/>
					)}
					</S.HeaderControls>
				)}
			</Base.SectionHeader>
			<Base.SectionContent>
				{colorTokens.length === 0 ? (
					<p>No color tokens found.</p>
				) : (
					<S.Base>
						{gridVisible && renderFamilyGrid(gridData.families, gridData.allShades)}
						{renderTileSection(tileTokens, gridVisible && hasGrid)}
					</S.Base>
				)}
			</Base.SectionContent>
		</Base.SectionBase>
	);
};

export default ColorsView;
